import moment from 'moment';
import { actionCodes, actionLabels, teamMemberRolesList } from 'constants/automation';
import { getApiIdFromAppUrl, getTenantDetails, parseRemiderPayload } from 'utils/utils';
import { generateDelayPayload } from './generateDelayPayload';
import { getFolderPaths } from './getFolderPaths';
import { AppConfig } from 'config/api_config';
import { setTimezoneWithoutAlteringTime } from 'utils/utility_functions/timezone';

const formatUserList = (userList = []) => {
  if (!userList?.length) return {};
  const accessible_list = [];
  const contact_assignees = [];
  const firm_user_assignees = [];
  const sub_task_groups = [];
  const message_to = [];
  const groups = [];
  userList?.forEach((user) => {
    if (['primary_contact', 'billing_contact'].includes(user?.value)) {
      contact_assignees.push(user?.value);
    } else if (teamMemberRolesList?.find((role) => role?.value === user?.value)) {
      firm_user_assignees.push(user?.value);
    } else if (user?.res_type === 'user-group') {
      sub_task_groups.push({ group_id: user?.value, sk: `user_group#${user?.value}` });
      groups.push({ group_id: user?.value, sk: `user_group#${user?.value}` });
    } else if (user?.res_type === 'user') {
      accessible_list.push(user?.value);
      message_to.push({ firm_user_id: user?.value });
    }
  });

  return {
    accessible_list,
    contact_assignees,
    firm_user_assignees,
    sub_task_groups,
    message_to,
    groups,
  };
};

export const generateActionPayload = (actions, triggerCode, timezone) => {
  const [tenantId, tenantSk] = getTenantDetails();
  return actions.map((action) => {
    if (triggerCode === 'TRIG_REVIEW_REQUEST' && action?.action_code === actionCodes.ACT_SEND_TEXT_MESSAGE) {
      return {
        delay: action?.delay ? generateDelayPayload(action?.delay) : undefined,
        action_code: action?.action_code,
        action_label: actionLabels[action?.action_code],
        action_description: {
          params: {
            qs_params: {
              case_id: '',
              tenant_id: tenantId,
              track_action_time: '$track_action_time',
              tenant_sk: tenantSk,
              manual_total_hours: '$manual_total_hours',
              activity_type_id: '$activity_type_id',
              file_name: '',
              file_path: '',
              file_type: '',
              size: '0',
              apiId: getApiIdFromAppUrl(AppConfig.baseURL2),
            },
            path_params: {},
          },
          body: {
            message_body: '$message_body',
            contact_assignees: ['$contact_assignees'],
          },
        },
      };
    } else if (triggerCode === 'TRIG_REVIEW_REQUEST' && action?.action_code === actionCodes.ACT_SEND_SECURE_MESSAGE) {
      return {
        delay: action?.delay ? generateDelayPayload(action?.delay) : undefined,
        action_code: action?.action_code,
        action_label: actionLabels[action?.action_code],
        action_description: {
          body: {
            creator_firm_user_id: '$creator_firm_user_id',
            data: {
              body: {
                subject: '$subject',
                message: '$message',
                contact_assignees: [],
                message_to: '$message_to',
                accessible_list: '$accessible_list',
                sub_task_groups: [],
                attachment: [],
              },
              secure_message_type: 'thread',
              tenant_id: tenantId,
            },
            action: 'message',
            type: 'secure_message',
          },
          params: {
            path_params: {},
            qs_params: {
              manual_total_hours: '$manual_total_hours',
              track_action_time: '$track_action_time',
              description: '$description',
              tenant_id: tenantId,
              tenant_sk: tenantSk,
              activity_type_id: '$activity_type_id',
              case_id: '',
            },
          },
        },
      };
    } else if (action?.action_code === actionCodes.ACT_SHARE_FORM) {
      // complete
      return {
        delay: action?.delay ? generateDelayPayload(action?.delay) : undefined,
        action_code: action?.action_code,
        action_label: actionLabels.ACT_SHARE_FORM,
        action_description: {
          body: {
            form_id: action?.form?.contact_form_id,
            form_name: action?.form?.form_name,
            contact_assignees: [action?.contact_assignees],
          },
          params: {
            path_params: {},
            qs_params: {
              send_email: 'true',
              tenant_id: tenantId,
              tenant_sk: tenantSk,
              form_id: action?.form?.contact_form_id,
            },
          },
        },
      };
    } else if (action?.action_code === actionCodes.ACT_SEND_SECURE_MESSAGE) {
      let creator_firm_user_id,
        creator_group_id,
        message_sender_assignee = '';
      if (teamMemberRolesList?.find((role) => role?.value === action?.creator_firm_user_id?.value)) {
        message_sender_assignee = action?.creator_firm_user_id?.value;
      } else if (action?.creator_firm_user_id?.res_type === 'user-group') {
        creator_group_id = action?.creator_firm_user_id?.value;
      } else {
        creator_firm_user_id = action?.creator_firm_user_id?.value;
      }
      return {
        delay: action?.delay ? generateDelayPayload(action?.delay) : undefined,
        action_code: action?.action_code,
        action_label: actionLabels.ACT_SEND_SECURE_MESSAGE,
        action_description: {
          body: {
            creator_firm_user_id: creator_firm_user_id,
            group_member_user_id: action?.group_member_user_id,
            creator_group_id,
            type: 'secure_message',
            data: {
              secure_message_type: 'thread',
              tenant_id: tenantId,
              body: {
                message_sender_assignee: message_sender_assignee,
                subject: action?.subject,
                message: action?.message,
                attachment: Array.isArray(action?.attachments)
                  ? action?.attachments?.map(({ display_name, file_name, file_path, size, file_type }) => ({
                      display_name,
                      file_name,
                      file_path,
                      size,
                      file_type,
                    }))
                  : [],
                ...formatUserList(action?.selectedMessageTo),
              },
            },
          },
          params: {
            qs_params: {
              tenant_id: tenantId,
              tenant_sk: tenantSk,
              case_id: '',
              track_action_time: action?.timeTracker?.track_action_time ? 'true' : 'false',
              manual_total_hours: action?.timeTracker?.manual_total_hours || '',
              notes: action?.timeTracker?.description || '',
              activity_type_id: action?.timeTracker?.activity_type_id || '',
            },
          },
        },
      };
    } else if (action?.action_code === actionCodes.ACT_BULK_UPLOAD_FOLDER) {
      return {
        delay: action?.delay ? generateDelayPayload(action?.delay) : undefined,
        action_code: action?.action_code,
        action_label: actionLabels.ACT_BULK_UPLOAD_FOLDER,
        action_description: {
          body: {
            folder_tree: { root: action?.folderStructure },
            type: 'bulk_upload',
            case_id: 'undefined',
            folder_id: '',
            not_linked_to_case: true,
            file_folder: getFolderPaths(action?.folderStructure),
          },
          params: {
            path_params: {},
            qs_params: {
              tenant_id: tenantId,
              tenant_sk: tenantSk,
              track_action_time: action?.timeTracker?.track_action_time ? 'true' : 'false',
              manual_total_hours: action?.timeTracker?.manual_total_hours || '',
              notes: action?.timeTracker?.description || '',
              activity_type_id: action?.timeTracker?.activity_type_id || '',
            },
          },
        },
      };
    } else if (action?.action_code === actionCodes.ACT_CREATE_DOCUMENT) {
      return {
        delay: action?.delay ? generateDelayPayload(action?.delay) : undefined,
        action_code: action?.action_code,
        action_label: actionLabels.ACT_CREATE_DOCUMENT,
        action_description: {
          body: {
            contact_assignees: action?.contact_assignees,
            document_format: action?.document_format,
            document_name: action?.document_name,
            send_mail: action?.send_mail,
            template: action?.template,
          },
          params: {
            path_params: {},
            qs_params: {
              tenant_id: tenantId,
              tenant_sk: tenantSk,
              track_action_time: action?.timeTracker?.track_action_time ? 'true' : 'false',
              manual_total_hours: action?.timeTracker?.manual_total_hours || '',
              notes: action?.timeTracker?.description || '',
              activity_type_id: action?.timeTracker?.activity_type_id || '',
            },
          },
        },
      };
    } else if (action?.action_code === actionCodes.ACT_TASK_CREATE) {
      const { message_to, ...rest } = formatUserList(action?.assignTo);
      let currentDateTime = moment().startOf('day').toDate();
      return {
        delay: action?.delay ? generateDelayPayload(action?.delay) : undefined,
        action_code: action?.action_code,
        action_label: actionLabels.ACT_TASK_CREATE,
        action_description: {
          body: {
            task_name: action?.task_name,
            description: action?.description,
            due_date_cycle: action?.due_date_cycle,
            due_date_period: action?.due_date_period,
            priority: action?.priority,
            reminder: parseRemiderPayload(action?.reminders),
            has_subtasks: action?.subTask?.length > 0,
            sub_task: Array.isArray(action?.subTask)
              ? action?.subTask?.map((subTask) => {
                  const { message_to: assign_to, ...rest2 } = formatUserList(subTask?.assignTo);
                  return {
                    sub_task_description: subTask?.sub_task_description,
                    sub_task_status: false,
                    ...rest2,
                    assign_to,
                  };
                })
              : [],
            ...rest,
            assign_to: message_to,
            assignTo: action?.assignTo,
            due_date: setTimezoneWithoutAlteringTime(currentDateTime, timezone),
            event_with_schedule_date: action?.event_with_schedule_date,
            event_preference: action?.event_with_schedule_date ? action?.event_preference : '',
          },
          params: {
            path_params: {},
            qs_params: {
              tenant_id: tenantId,
              tenant_sk: tenantSk,
              track_action_time: action?.timeTracker?.track_action_time ? 'true' : 'false',
              manual_total_hours: action?.timeTracker?.manual_total_hours || '',
              notes: action?.timeTracker?.description || '',
              activity_type_id: action?.timeTracker?.activity_type_id || '',
            },
          },
        },
      };
    } else if (action?.action_code === actionCodes.ACT_EVENT_CREATE) {
      const { message_to, ...rest } = formatUserList(action?.assignTo);
      return {
        delay: action?.delay ? generateDelayPayload(action?.delay) : undefined,
        action_code: action?.action_code,
        action_label: actionLabels.ACT_EVENT_CREATE,
        action_description: {
          body: {
            title: action?.title,
            attachments: Array.isArray(action?.attachments)
              ? action?.attachments?.map(({ display_name, file_name, file_path, size, file_type }) => ({
                  display_name,
                  file_name,
                  file_path,
                  size,
                  file_type,
                }))
              : [],
            case_name: '',
            category: '',
            conferencing_url: action?.meeting_url,
            description: action?.description,
            event_type: action?.event_type,
            location: action?.location,
            meeting_cycle: action?.meeting_cycle,
            meeting_due_date_period: action?.meeting_due_date_period,
            reminder: parseRemiderPayload(action?.reminders),
            meeting_start_time: setTimezoneWithoutAlteringTime(action?.eventStartTime, timezone),
            meeting_end_time: setTimezoneWithoutAlteringTime(action?.eventEndTime, timezone),
            not_linked_to_case: false,
            guests: message_to,
            event_with_schedule_date: action?.event_with_schedule_date,
            event_preference: action?.event_with_schedule_date ? action?.event_preference : '',
            assignee_reminder: action?.assignee_reminder || [],
            ...rest,
          },
          params: {
            qs_params: {
              tenant_id: tenantId,
              tenant_sk: tenantSk,
            },
          },
        },
      };
    } else if (action?.action_code === actionCodes.ACT_SEND_TEXT_MESSAGE) {
      return {
        delay: action?.delay ? generateDelayPayload(action?.delay) : undefined,
        action_code: action?.action_code,
        action_label: actionLabels.ACT_SEND_TEXT_MESSAGE,
        action_description: {
          body: {
            contact_assignees: action?.contact_assignees,
            attachments: Array.isArray(action?.attachments)
              ? action?.attachments?.map(({ display_name, file_name, file_path, size, file_type }) => ({
                  display_name,
                  file_name,
                  file_path,
                  size,
                  file_type,
                }))
              : [],
            message_body: action?.message_body,
          },
          params: {
            qs_params: {
              tenant_id: tenantId,
              tenant_sk: tenantSk,
              apiId: getApiIdFromAppUrl(AppConfig.baseURL2),
              track_action_time: action?.timeTracker?.track_action_time ? 'true' : 'false',
              manual_total_hours: action?.timeTracker?.manual_total_hours || '',
              notes: action?.timeTracker?.description || '',
              activity_type_id: action?.timeTracker?.activity_type_id || '',
              display_filename: action?.attachments?.[0]?.display_filename || '',
              file_path: action?.attachments?.[0]?.file_path || '',
              media_content_type: action?.attachments?.[0]?.file_type || '',
              size: String(action?.attachments?.[0]?.size || 0),
            },
          },
        },
      };
    } else if (action?.action_code === actionCodes.ACT_SEND_EMAIL) {
      return {
        delay: action?.delay ? generateDelayPayload(action?.delay) : undefined,
        action_code: action?.action_code,
        action_label: actionLabels.ACT_SEND_EMAIL,
        action_description: {
          body: {
            contact_assignees: [...(action?.recipient_list || []), ...(action?.cc_list || [])]?.filter(
              (item) => item === 'primary_contact' || item === 'billing_contact'
            ),
            subject: action?.subject,
            recipient_list: action?.recipient_list,
            cc_list: action?.cc_list,
            email_content: action?.email_content,
            attachments: Array.isArray(action?.attachments)
              ? action?.attachments?.map(({ display_name, file_name, file_path, size, file_type }) => ({
                  display_name,
                  file_name,
                  file_path,
                  size,
                  file_type,
                }))
              : [],
            message_sender_assignee: action?.message_sender_assignee,
            bcc_list: [],
            lawft_assignees: [...(action?.recipient_list || []), ...(action?.cc_list || [])]?.filter(
              (item) => !(item === 'primary_contact' || item === 'billing_contact')
            ),
          },
          params: {
            qs_params: {
              tenant_id: tenantId,
              tenant_sk: tenantSk,
            },
          },
        },
      };
    }
  });
};
